import { Capacitor, registerPlugin, type PluginListenerHandle } from '@capacitor/core'
import { publicAppUrl } from './serverOrigin'
import type { AuthSession } from './types'

export type NativeCallSnapshot = {
  callId: string
  peerId: string
  peerName: string
  direction: 'in' | 'out'
  status: 'outgoing' | 'incoming' | 'connecting' | 'active' | 'ended'
  muted: boolean
  startedAt: number | null
  message: string
  mode: 'audio' | 'video'
  minimized: boolean
  cameraEnabled: boolean
  cameraFacing: 'user' | 'environment'
  transport: 'native'
}
export type NativeCallEvent = { type: 'state'; call: NativeCallSnapshot | null }
export type NativeVideoRect = { x: number; y: number; width: number; height: number }
type CallId = { callId: string }
type NativeState = { call: NativeCallSnapshot | null }
interface CallsPlugin {
  configure(options: { token: string; userId: string; userName: string; baseURL: string }): Promise<void>
  logout(): Promise<void>
  getState(): Promise<NativeState>
  refresh(): Promise<void>
  start(options: CallId & { peerId: string; peerName: string; mode: 'audio' | 'video'; iceServers: RTCIceServer[] }): Promise<void>
  accept(options: CallId): Promise<void>
  reject(options: CallId): Promise<void>
  end(options: CallId): Promise<void>
  setMuted(options: CallId & { muted: boolean }): Promise<void>
  setCameraEnabled(options: CallId & { enabled: boolean }): Promise<void>
  switchCamera(options: CallId): Promise<void>
  setMinimized(options: CallId & { minimized: boolean }): Promise<void>
  setVideoLayout(options: { remote: NativeVideoRect | null; local: NativeVideoRect | null }): Promise<void>
  addListener(name: 'callState', listener: (state: NativeState) => void): Promise<PluginListenerHandle>
}

// Keep the proxy synchronous: Capacitor proxies expose a synthetic `then` method.
// Native media and signalling belong to the Swift engine, including in background.
const Calls = registerPlugin<CallsPlugin>('ShixuCalls')
let sessionGeneration = 0
let sessionMutations: Promise<void> = Promise.resolve()

export function isNativeCallAvailable(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('ShixuCalls')
}
function available() {
  if (!isNativeCallAvailable()) throw new Error('当前安装版本尚不支持原生音视频通话，请更新诗序')
}
async function withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    return await Promise.race([promise, new Promise<never>((_, reject) => { timer = setTimeout(() => reject(new Error(message)), ms) })])
  } finally { if (timer) clearTimeout(timer) }
}
function sessionMutation(work: () => Promise<void>): Promise<void> {
  // Order actual native mutations, even when a UI deadline has expired. A late
  // configure must never restore a session after logout has cleared Keychain.
  const task = sessionMutations.then(work, work)
  sessionMutations = task.catch(() => {})
  return task
}
function snapshot(value: NativeCallSnapshot | null | undefined): NativeCallSnapshot | null {
  if (!value) return null
  if (typeof value.callId !== 'string' || !value.callId || typeof value.peerId !== 'string' ||
      !['outgoing', 'incoming', 'connecting', 'active', 'ended'].includes(value.status) ||
      (value.direction !== 'in' && value.direction !== 'out')) return null
  return {
    callId: value.callId, peerId: value.peerId, peerName: value.peerName || '诗友',
    direction: value.direction, status: value.status, muted: value.muted === true,
    startedAt: typeof value.startedAt === 'number' && Number.isFinite(value.startedAt) ? value.startedAt : null,
    message: typeof value.message === 'string' ? value.message : '', mode: value.mode === 'video' ? 'video' : 'audio',
    minimized: value.minimized === true, cameraEnabled: value.cameraEnabled === true,
    cameraFacing: value.cameraFacing === 'environment' ? 'environment' : 'user', transport: 'native',
  }
}

export async function initializeNativeCalls(session: AuthSession, listener: (event: NativeCallEvent) => void): Promise<() => void> {
  available()
  const generation = ++sessionGeneration
  let disposed = false
  let eventVersion = 0
  const handle = await withTimeout(Calls.addListener('callState', (state) => {
    if (disposed || generation !== sessionGeneration) return
    eventVersion += 1
    listener({ type: 'state', call: snapshot(state.call) })
  }), 8_000, '音视频通话组件加载超时，请重新打开诗序')
  const stop = () => { disposed = true; void handle.remove().catch(() => {}) }
  try {
    await withTimeout(sessionMutation(async () => {
      if (generation !== sessionGeneration || disposed) return
      await Calls.configure({ token: session.token, userId: session.user.id, userName: session.user.nickname, baseURL: new URL(publicAppUrl('/')).origin })
    }), 12_000, '通话会话初始化超时，请重新打开诗序')
    if (generation !== sessionGeneration) { stop(); return stop }
    const version = eventVersion
    const state = await withTimeout(Calls.getState(), 5_000, '读取当前通话超时')
    if (!disposed && generation === sessionGeneration && version === eventVersion) listener({ type: 'state', call: snapshot(state.call) })
    return stop
  } catch (error) { stop(); throw error }
}

/** Logout clears native credentials/media; removing a JS listener does neither. */
export async function disconnectNativeCalls(): Promise<void> {
  ++sessionGeneration
  if (!isNativeCallAvailable()) return
  await withTimeout(sessionMutation(() => Calls.logout()), 8_000, '音视频通话退出超时')
}
export async function refreshNativeCalls(): Promise<void> { available(); await Calls.refresh() }
export async function startNativeCall(options: CallId & { peerId: string; peerName: string; mode: 'audio' | 'video'; iceServers: RTCIceServer[] }): Promise<void> {
  available()
  await Calls.start(options)
}
export async function acceptNativeCall(options: CallId): Promise<void> { available(); await Calls.accept(options) }
export async function rejectNativeCall(callId: string): Promise<void> { available(); await Calls.reject({ callId }) }
export async function endNativeCall(callId: string): Promise<void> { available(); await Calls.end({ callId }) }
export async function setNativeCallMuted(callId: string, muted: boolean): Promise<void> { available(); await Calls.setMuted({ callId, muted }) }
export async function setNativeCallCameraEnabled(callId: string, enabled: boolean): Promise<void> { available(); await Calls.setCameraEnabled({ callId, enabled }) }
export async function switchNativeCallCamera(callId: string): Promise<void> { available(); await Calls.switchCamera({ callId }) }
export async function setNativeCallMinimized(callId: string, minimized: boolean): Promise<void> { available(); await Calls.setMinimized({ callId, minimized }) }
export async function setNativeCallVideoLayout(options: { remote: NativeVideoRect | null; local: NativeVideoRect | null }): Promise<void> {
  if (!isNativeCallAvailable()) return
  function rect(value: NativeVideoRect | null): NativeVideoRect | null {
    if (!value || !Object.values(value).every(Number.isFinite) || value.width <= 0 || value.height <= 0) return null
    return { x: value.x, y: value.y, width: value.width, height: value.height }
  }
  await Calls.setVideoLayout({ remote: rect(options.remote), local: rect(options.local) })
}
